import { Injectable, OnDestroy } from '@angular/core';
import { Action, Select, State, StateContext } from '@ngxs/store';
import { Observable, Subject, catchError, map, takeUntil, tap } from 'rxjs';

import { DownloadsStateModel, downloadsInitialState } from "@store/models/downloads.model";
import { DownloadsActions } from '@store/actions/downloads.actions';

import { SettingsSelectors } from '@store/selectors/settings.selectors';
import { DownloadFileType } from '@core/enums';
import { Download } from '@models/download.model';
import { PeriodsI } from '@models/periods';

@State<DownloadsStateModel>({
    name: 'downloads',
    defaults: downloadsInitialState
})
@Injectable()
export class DownloadsState implements OnDestroy {
    private readonly destroy$: Subject<void> = new Subject<void>();

    @Select(SettingsSelectors.settingsPeriods) settingsPeriods$!: Observable<PeriodsI>;

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @Action(DownloadsActions.LoadDownloads)
    loadDownloads(ctx: StateContext<DownloadsStateModel>) {
        const state = ctx.getState();
        ctx.patchState({ ...state, loading: true, loaded: false, error: null });

       return this.getLocalDownloadFiles()
        .pipe(
            takeUntil(this.destroy$),
            tap((downloads) => {
                ctx.patchState({ downloads, loading: false, loaded: true, error: null});
            }),
            catchError((error) => {
                ctx.patchState({ loading: false, loaded: true, error: error.message });
                throw error;
            }))
    }

    getLocalDownloadFiles(): Observable<Download[]> {
        return this.settingsPeriods$
            .pipe(
                takeUntil(this.destroy$),
                map(settings => {
                    return [
                        {
                            title: 'Praktische handleiding',
                            extension: 'PDF',
                            description: 'Lees in dit document alles over de voorbereiding en de afname. Neem de handleiding alvast door zodat je weet wat je straks kunt verwachten.',
                            fileType: DownloadFileType.Document,
                            display: settings ? settings['pdfBeschikbaar'].withinRange : false,
                            url: 'https://cito.nl/media/2vrbb33y/lib_dst_prakt_handleiding_2025_02.pdf',
                        },
                        {
                            title: 'Toetsreglement',
                            extension: 'PDF',
                            description: 'In dit document vind je alle informatie over de Leerling in beeld – doorstroomtoets. Neem het document alvast door zodat je op de hoogte bent.',
                            fileType: DownloadFileType.Document,
                            display: settings ? settings['pdfBeschikbaar'].withinRange : false,
                            url: 'https://cito.nl/media/ntjdmmtk/lib_dst_toetsreglement_2025_def.pdf',
                        },
                        {
                            title: 'Audio-bestanden papieren toets',
                            extension: 'ZIP',
                            description: 'Download hier de audio-bestanden van de papieren toets voor de leerlingen die auditieve ondersteuning nodig hebben bij de opgaven en teksten. Belangrijk! Zorg dat je na de afname de audiobestanden weer verwijdert.',
                            fileType: DownloadFileType.Audio,
                            display: settings ? settings['audioBeschikbaar'].withinRange : false,
                        },
                        {
                            title: 'Voorbeeldtoetsen papier',
                            extension: 'ZIP',
                            description: 'Hier vind je twee voorbeeldtoetsen (kleur en zwart-wit). Als je leerlingen de toets op papier maken, is het verstandig om de voorbeeldopgaven voor de afname klassikaal met de leerlingen te bespreken. Je kunt ze laten zien op het digibord en/of printen voor elke leerling. Ook is er audio beschikbaar voor de voorbeeldtoets.',
                            fileType: DownloadFileType.AfnamePapier,
                            display: settings ? settings['pdfBeschikbaar'].withinRange : false,
                            url: 'https://cito.nl/media/l0dhl2ev/libdst_voorbeeldtoetsen-papier-2024-2025.zip',
                        },
                        {
                            title: 'Voorbeeldtoets digitaal regulier',
                            extension: 'URL',
                            description: 'Met deze link ga je naar de digitale voorbeeldtoets. Als je leerlingen de toets digitaal maken, is het verstandig om deze voorbeeldtoets een dag of enkele dagen voor de afname klassikaal te doorlopen of door de leerlingen zelf te laten maken.',
                            fileType: DownloadFileType.AfnameDigitaal,
                            display: settings ? settings['pdfBeschikbaar'].withinRange : false,
                            url: 'https://afname.cito.nl/direct/launch/TST-DST2025_voorbeeldtoets',
                        },
                        {
                            title: 'Voorbeeldtoets digitaal met audio-ondersteuning',
                            extension: 'URL',
                            description: 'Met deze link ga je naar de digitale voorbeeldtoets. Als je leerlingen de toets digitaal maken, is het verstandig om deze voorbeeldtoets een dag of enkele dagen voor de afname klassikaal te doorlopen of door de leerlingen zelf te laten maken.',
                            fileType: DownloadFileType.AfnameDigitaal,
                            display: settings ? settings['pdfBeschikbaar'].withinRange : false,
                            url: 'https://afname.cito.nl/direct/launch/TST-DST2025_voorbeeldtoets_audio',
                        }
                    ];
                })
            );
    }
}
